import { useRef, useMemo } from "react";
import classNames from "classnames";
import { useGlobalBanner } from "@ugitgud/legos/components/banner/BannerContainer";
import { CURRENT_ACTIVE_BANNER } from "@ugitgud/legos/hooks/useBanner";
import { ModuleSection, SafeModuleSection } from "@ugg/shared/components/YearInReview/components/ModuleSection";
import { PlayerLandingModule } from "../components/PlayerLandingModule";
import { YearInReviewLPGraph } from "../components/YearInReviewLPGraph";
import { PlayTimeChart } from "../components/PlayTimeChart";
import { SvgYearInReviewLogoFull } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewLogoFull";
import { ShareButton } from "../components/ShareButton";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { WardStatsModule } from "../components/WardStatsModule";
import { RoleBreakdownModule, RankStats } from "../components/RoleBreakdownModule";
import { TeamPlayersModule } from "../components/TeamPlayersModule";
import { PingStatsModule } from "../components/PingStatsModule";
import { StatCardModule } from "../components/StatCardModule";
import { ChampStatsModule } from "../components/ChampStatsModule";
import { AramChampStatsModule, AramChampionStats } from "./AramChampStatsModule";
import {
  transformChampionRanking,
  transformGameLogs,
  transformPingStats,
  transformRoleStats,
  transformStatsForCard,
  transformTopFriends,
  transformWardStats,
  transformAramChampStats,
  transformAramStatsForCard,
} from "../components/helpers/year-in-review-data-transformation";
import { transformLPTimeline } from "../components/helpers/year-in-review-lp-helpers";
import { getUserYearInReviewData } from "@ugg/shared/api/requests/summoner-profiles/year-in-review";
import { getRankStatsForAllRoles } from "./helpers/year-in-review-rank-data-extraction";
import { useState, useEffect } from "react";

export const YearInReviewContent = ({
  region,
  riotUserName,
  riotTagLine,
  iconId,
}: {
  region: string;
  riotUserName: string;
  riotTagLine: string;
  iconId?: number;
}) => {
  const firstModuleRef = useRef<HTMLDivElement>(null);
  const { openBannerIds } = useGlobalBanner();
  const isBannerOpen = openBannerIds.includes(CURRENT_ACTIVE_BANNER);

  const scrollToFirstModule = () => {
    firstModuleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const { data: yearInReviewData, loading, error } = getUserYearInReviewData(riotUserName, riotTagLine, region);
  const [rankData, setRankData] = useState<RankStats | null>(null);
  const [rankDataLoading, setRankDataLoading] = useState(true);

  useEffect(() => {
    async function fetchRankData() {
      try {
        const data = await getRankStatsForAllRoles();
        setRankData(data);
      } catch (e) {
        console.error("Failed to load rank stats:", e);
        setRankData(null);
      } finally {
        setRankDataLoading(false);
      }
    }
    fetchRankData();
  }, []);

  const transformedData = useMemo(() => {
    const srDataExists = yearInReviewData && yearInReviewData.total_games > 10 ? true : false;
    const aramDataExists = yearInReviewData && yearInReviewData.aram_data.total_games > 10 ? true : false;

    if (!yearInReviewData) {
      return null;
    } else if (srDataExists && aramDataExists) {
      return {
        lpSeries: transformLPTimeline(yearInReviewData.lp_timeline),
        gameLogs: transformGameLogs(yearInReviewData),
        statsForCard: transformStatsForCard(yearInReviewData),
        aramStatsForCard: transformAramStatsForCard(yearInReviewData),
        roleStats: transformRoleStats(yearInReviewData),
        topFriends: transformTopFriends(yearInReviewData),
        championRanking: transformChampionRanking(yearInReviewData),
        pingStats: transformPingStats(yearInReviewData),
        wardStats: transformWardStats(yearInReviewData),
        aramStats: transformAramChampStats(yearInReviewData),
      };
    } else if (aramDataExists && !srDataExists) {
      return {
        aramStats: transformAramChampStats(yearInReviewData),
        pingStats: transformPingStats(yearInReviewData),
        gameLogs: transformGameLogs(yearInReviewData),
        aramStatsForCard: transformAramStatsForCard(yearInReviewData),
      };
    } else {
      return {
        lpSeries: transformLPTimeline(yearInReviewData.lp_timeline),
        gameLogs: transformGameLogs(yearInReviewData),
        statsForCard: transformStatsForCard(yearInReviewData),
        roleStats: transformRoleStats(yearInReviewData),
        topFriends: transformTopFriends(yearInReviewData),
        championRanking: transformChampionRanking(yearInReviewData),
        pingStats: transformPingStats(yearInReviewData),
        wardStats: transformWardStats(yearInReviewData),
      };
    }
  }, [yearInReviewData]);

  const availableModules = useMemo(() => {
    if (!transformedData) return {};
    const {
      lpSeries,
      gameLogs,
      statsForCard,
      roleStats,
      topFriends,
      championRanking,
      pingStats,
      wardStats,
      aramStats,
      aramStatsForCard,
    } = transformedData;

    return {
      lpGraph: Array.isArray(lpSeries) && lpSeries.length > 0,
      playTime: Array.isArray(gameLogs) && gameLogs.length > 0,
      statCard: statsForCard !== null && statsForCard !== undefined,
      roleBreakdown: roleStats !== null && roleStats !== undefined,
      teamPlayers: Array.isArray(topFriends) && topFriends.length > 0,
      champStats: Array.isArray(championRanking) && championRanking.length > 0,
      pingStats: pingStats !== null && pingStats !== undefined,
      wardStats: wardStats !== null && wardStats !== undefined,
      aramStats: Array.isArray(aramStats) && aramStats.length > 0,
      aramStatsForCard: aramStatsForCard !== null && aramStatsForCard !== undefined,
    };
  }, [transformedData]);

  if (loading || rankDataLoading) {
    return (
      <div className="w-full flex justify-center h-[600px] p-[18px] max-md:p-[12px] rounded-[3px]">
        <div className="spinthatshit-loader">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!transformedData || error || !yearInReviewData) {
    return null;
  }

  const {
    lpSeries,
    gameLogs,
    statsForCard,
    roleStats,
    topFriends,
    championRanking,
    pingStats,
    wardStats,
    aramStats,
    aramStatsForCard,
  } = transformedData;

  return (
    <>
      <PlayerLandingModule
        userName={riotUserName}
        tagLine={riotTagLine}
        iconId={iconId}
        onScrollToModules={scrollToFirstModule}
      />
      {!loading ? (
        <div ref={firstModuleRef} className={classNames("scroll-mt-[95px]", { "scroll-mt-[142px]": isBannerOpen })}>
          {availableModules.lpGraph && (
            <SafeModuleSection
              preHeader="Sacred Internet points"
              title="It's all about LP"
              subtitle="An entire year of grinding condensed into the width of your browser. Did your hard work pay off?"
            >
              <YearInReviewLPGraph series={lpSeries!} />
            </SafeModuleSection>
          )}

          {availableModules.playTime && (
            <SafeModuleSection
              preHeader="Time Sink, Visualized."
              title="It's Play Time"
              subtitle="We've got you covered every day of the year. Let's see how much you played. Notice any trends?"
            >
              <PlayTimeChart activityData={gameLogs} />
            </SafeModuleSection>
          )}

          {(availableModules.statCard || availableModules.aramStatsForCard) && (
            <SafeModuleSection
              preHeader="Back To Basics"
              title="Stat Card"
              subtitle="Dive in to your overall stats in each game mode. See how stack up against the boosted animals you call friends."
            >
              <StatCardModule aramStats={aramStatsForCard} srStats={statsForCard} />
            </SafeModuleSection>
          )}

          {availableModules.roleBreakdown && (
            <SafeModuleSection
              preHeader="A Master(?) Across the Map"
              title="Position Breakdown"
              subtitle="Here's your chance to prove that you don't just instalock mid... right?"
            >
              <RoleBreakdownModule playerStats={roleStats!} rankData={rankData} />
            </SafeModuleSection>
          )}

          {availableModules.teamPlayers && (
            <SafeModuleSection
              preHeader="Your best... or worst friends"
              title="team players"
              subtitle="League is always more fun with friends. Let's see how well you did with some of your most common duos."
            >
              <TeamPlayersModule friendData={topFriends!} />
            </SafeModuleSection>
          )}

          {availableModules.champStats && (
            <SafeModuleSection>
              <ChampStatsModule championData={championRanking!} />
            </SafeModuleSection>
          )}

          {availableModules.aramStats && (
            <SafeModuleSection
              preHeader="All random, all mid"
              title="aram favorites"
              subtitle="Of the 170 champions in League, these are your most rolled. Maybe it’s not so random..."
            >
              <AramChampStatsModule stats={aramStats!} />
            </SafeModuleSection>
          )}

          {availableModules.pingStats && (
            <SafeModuleSection
              preHeader="The Only Way to Communicate"
              title="Ping Smurf"
              subtitle="Anything you want to say can be said with pings. Can you guess which ping you've used the most?"
            >
              <PingStatsModule pingStats={pingStats} />
            </SafeModuleSection>
          )}

          {availableModules.wardStats && (
            <SafeModuleSection
              preHeader="Knowledge is Power"
              title="Vision & Control"
              subtitle="Let's see how well you control the rift"
            >
              <WardStatsModule {...wardStats!} />
            </SafeModuleSection>
          )}

          <div className="flex justify-center">
            <Icon
              icon={<SvgYearInReviewLogoFull />}
              className="text-[#D3A752] xs:w-[207px] xs:h-[200px] w-[132px] h-[128px] mt-[180px]"
            />
          </div>
          <ModuleSection
            preHeader="Time to Flex"
            title="Share Your Year In Review"
            subtitle="You've put in the work this year and we're happy to have been your companion! Let's show off those numbers."
            className="mt-[60px] mb-[100px]"
            sectionDivider={false}
          >
            <ShareButton region={region} riotUserName={riotUserName} riotTagLine={riotTagLine} />
          </ModuleSection>
        </div>
      ) : (
        <ShareButton region={region} riotUserName={riotUserName} riotTagLine={riotTagLine} />
      )}
    </>
  );
};
